/* @flow */

//----------------------------------------------------------------------//
//                         Imported NPM modules                         //
//----------------------------------------------------------------------//

import Moment from 'moment'; // www.npmjs.com/package/moment
import { when } from 'mobx';

//----------------------------------------------------------------------//
//                        Imported local modules                        //
//----------------------------------------------------------------------//

import AuthenticationStore from 'stores/AuthenticationStore.js';
import UserStore from 'stores/UserStore.js';
import { Roles } from 'permissions/defs';
import type { RoleId } from 'permissions/defs';

//----------------------------------------------------------------------//
//                          Private functions                           //
//----------------------------------------------------------------------//

function mapUserRoleIdToGroup(userRoleId: RoleId): string {
  switch (userRoleId) {
    case Roles.USER:
    case Roles.MANAGER:
    case Roles.PURCHASER:
    case Roles.VIEW_ONLY:
    case Roles.ACCOUNT_ADMIN:
      return 'Customer';
    case Roles.ACCOUNT_MANAGER:
    case Roles.ASSET_MANAGER:
    case Roles.BUSINESS_VIEW_ONLY:
    case Roles.SYSTEM_ADMIN:
    case Roles.SYSTEM_ADMIN_REGIONAL:
      return 'Employee';
    default:
      return '';
  }
}

//----------------------------------------------------------------------//
//                            Exported class                            //
//----------------------------------------------------------------------//

/**
 * @see help.inlinemanual.com/docs/single-page-app-and-people-tracking-angular-react-ember
 * @see help.inlinemanual.com/docs/enabling-people-tracking
 * @see help.inlinemanual.com/docs/automatically-launch-tutorial-new-users
 */
export default class InlineManualService {
  isUpdatedHandlerEnabled = false;

  async initialise(
    mrrEnhancedHistory: Object,
    authenticationStore: AuthenticationStore,
    userStore: UserStore
  ): Promise<*> {
    try {
      await this.loadLibrary();
      await this.initForUser(authenticationStore, userStore);
      /** @see github.com/alisd23/mobx-react-router/tree/v4.0.2#synchistorywithstorehistory-store */
      mrrEnhancedHistory.subscribe(() => {
        this.handleUpdate();
      });
    } catch (e) {
      console.log(e);
    }
  }

  /**
   * "Include the Inline Manual JS library file in your HTML head element."
   *
   * @see help.inlinemanual.com/docs/single-page-app-and-people-tracking-angular-react-ember
   */
  loadLibrary(): Promise<*> {
    /**
     * @see /scripts/build-env-vars.js
     * @see github.com/facebook/create-react-app/tree/v1.0.17/packages/react-scripts/template#adding-custom-environment-variables
     */
    const apiKey = process.env.REACT_APP_INLINEMANUAL_KEY;

    if (!apiKey) {
      return Promise.reject('Failed to load Inline Manual library due to missing api key');
    }

    const scriptUrl = 'https://inlinemanual.com/embed/player.' + apiKey + '.bare.js';

    /** @see stackoverflow.com/a/8578840 */
    return new Promise(resolve => {
      const script = document.createElement('script');

      script.async = true;
      script.onload = resolve;
      script.charset = 'UTF-8';
      script.src = scriptUrl;

      document.getElementsByTagName('head')[0].appendChild(script);
    });
  }

  /**
   * "Once the user is logged in, you can prepare the inlineManualTracking object."
   * "To initialize the Player for the very first time, call: createInlineManualPlayer(window.inlineManualPlayerData)"
   *
   * @see help.inlinemanual.com/docs/single-page-app-and-people-tracking-angular-react-ember
   */
  async initForUser(authenticationStore: AuthenticationStore, userStore: UserStore): Promise<*> {
    await when(() => userStore.userLoaded);
    const { activeUser } = userStore;

    if (!activeUser) {
      return;
    }

    if (!window.createInlineManualPlayer) {
      console.warn(
        'Inline Manual failed to load due to lack of library method `createInlineManualPlayer`'
      );
      return; // InlineManual is not available (eg: script failed to load).
    }

    const userCreationDatetime: string = activeUser.created_at; // Example: "2018-07-17 09:42:16"

    const userCreationMoment: Moment = Moment.utc(userCreationDatetime);

    const userCreationUnixSecs: number = userCreationMoment.unix();

    window.inlineManualTracking = {};

    /**
     * "The `uid` is the only field that is required and can be anything that is
     * unique for the current user - a string, integer, number, e-mail etc."
     *
     * @see help.inlinemanual.com/docs/automatically-launch-tutorial-new-users
     */
    window.inlineManualTracking.uid = activeUser.id;

    /**
     * "The `created` field is in UNIX Timestamp format in seconds."
     *
     * @see help.inlinemanual.com/docs/automatically-launch-tutorial-new-users
     */
    window.inlineManualTracking.created = userCreationUnixSecs;

    window.createInlineManualPlayer(window.inlineManualPlayerData);

    this.enableUpdatesHandler();
  }

  /**
   * "When the application moves to a different page (or there is a state change) where you want to re-check the
   * metadata and automation, you will need to call update method -- inline_manual_player.update() -- This will check
   * for the latest profile with stats about plays and then trigger re-running the autolaunchers against latest data."
   *
   * @see help.inlinemanual.com/docs/single-page-app-and-people-tracking-angular-react-ember
   */
  handleUpdate() {
    if (
      this.isUpdatedHandlerEnabled &&
      window.inline_manual_player &&
      window.inline_manual_player.update
    ) {
      window.inline_manual_player.update();
    }
  }

  /**
   * It seems that window.inline_manual_player.update() should not be called until window.createInlineManualPlayer()
   * has been called, and that can't happen until window.inlineManualTracking has been created, and that can't happen
   * until the user has logged in (because window.inlineManualTracking.uid requires the user's email address).
   *
   * So once window.createInlineManualPlayer() has been called, then it seems window.inline_manual_player should now
   * be available, and so it's now safe for window.inline_manual_player.update() to be called.
   *
   * @see help.inlinemanual.com/docs/single-page-app-and-people-tracking-angular-react-ember
   */
  enableUpdatesHandler() {
    this.isUpdatedHandlerEnabled = true;
  }

  /**
   * It's unclear what should be done when the user logs out.  Since window.inline_manual_player seems to be
   * associated with a specific user (via window.createInlineManualPlayer() and window.inlineManualTracking.uid),
   * then when the user logs out, presumably window.inline_manual_player.update() should no longer be called for any
   * subsequent update events.
   *
   * @see help.inlinemanual.com/docs/single-page-app-and-people-tracking-angular-react-ember
   */
  disableUpdatesHandler() {
    this.isUpdatedHandlerEnabled = false;
  }
}
